import { useState } from "react";
import {
  Button,
  colors,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography,
} from "@mui/material";

import { useMaterialUIController } from "context";

import MDBadge from "components/MDBadge";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";

import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditIcon from "@mui/icons-material/Edit";
import categoriaDefault from "helpers/categoriaDefault";

import { ILotes } from "interfaces/lotes";
import { IEditCategoria, IEventoResponse } from "interfaces/eventos";

import ModalCategoria from "./ModalCategoria";
import {
  createCategoria,
  deleteCategoria,
  updateCategoria,
} from "services/evento";
import { getColorsTags } from "helpers/getColorsTags";
import { formatCurrency } from "helpers/utils";

interface Props {
  evento: IEventoResponse;
  listCategorias: IEditCategoria[];
  handleSelectedListCategoria: (cat: IEditCategoria[]) => void;
  isEdit: boolean;
  distanciaId?: number;
}

/**
 * Componente principal FormDistancia que gerencia a exibição, criação, edição e remoção
 * de categorias associadas a um evento. Ele permite que o usuário defina as categorias
 * de acordo com seus lotes de eventos, sexo e outras configurações fornecidas.
 *
 * @param {Props} evento - O objeto do evento contendo as informações e lotes associados.
 */
export default function FormCategoria({
  evento,
  listCategorias,
  handleSelectedListCategoria,
  isEdit,
  distanciaId,
}: Props) {
  const [controller] = useMaterialUIController();
  const { dataConfig } = controller;
  const [openModal, setOpenModal] = useState<boolean>(false);
  const [editCategoria, setEditCategoria] = useState<IEditCategoria | null>(
    null
  );
  const [indexEdit, setIndexEdit] = useState<number | null>(null);

  const lotes = evento?.lotesEventos || [];
  const valoresTags = getTagsLotes({ lotes });
  const listSexos = dataConfig.tipoSexo;

  const handleLoadingDefault = () =>
    handleSelectedListCategoria(categoriaDefault);

  const handleDeleteCategoria = async (index: number) => {
    try {
      //Se EDIT = true, deletar também do banco de dados
      if (isEdit) await deleteCategoria(listCategorias[index].id);
      const list = [...listCategorias];
      list.splice(index, 1);
      handleSelectedListCategoria(list);
    } catch (error: any) {
      alert(error.message);
    }
  };

  const handleEditCategoria = (index: number) => {
    const item = listCategorias[index];
    setIndexEdit(index);
    setEditCategoria(item);
    setOpenModal(true);
  };

  const handleNewCategoria = () => {
    setEditCategoria(null);
    setIndexEdit(null);
    setOpenModal(true);
  };

  const handleAddCategoria = async (categoria: IEditCategoria) => {
    if (editCategoria) {
      const list = [...listCategorias];
      list[indexEdit] = categoria;
      handleSelectedListCategoria(list);
      setIndexEdit(null);
      setEditCategoria(null);
      setOpenModal(false);
      if (isEdit) updateCategoria(categoria);
    } else {
      const newCategoria = isEdit
        ? await createCategoria(categoria, distanciaId)
        : categoria;
      handleSelectedListCategoria([...listCategorias, newCategoria]);
    }
  };

  const getValoresTags = (valoresTags: any, tagId: number) => {
    if (!valoresTags || !tagId) return "INEXISTENTE";
    const listValores = valoresTags.find(
      (tag: any) => tag.value === tagId.toString()
    );

    if (listValores) {
      return listValores.label;
    }
    return "INEXISTENTE";
  };

  return (
    <MDBox
      bgColor="grey-100"
      border="dashed 1px grey"
      borderRadius="lg"
      sx={{ my: 2, p: 3 }}
    >
      <MDBox display="flex" justifyContent="space-between">
        <Typography variant="h6" sx={{ mb: 2 }}>
          Definir as categorias
        </Typography>
        <MDBox display="flex" alignItems="center" gap={2}>
          <Button onClick={handleNewCategoria}>Nova Categoria</Button>
          {/* Modal para adicionar as categorias */}
          <ModalCategoria
            open={openModal}
            handleOpen={() => setOpenModal(!openModal)}
            handleAddCategoria={handleAddCategoria}
            tags={valoresTags}
            edit={editCategoria}
          />

          <MDButton variant="text" color="info" onClick={handleLoadingDefault}>
            Carregar padrão
          </MDButton>
        </MDBox>
      </MDBox>

      <TableContainer component={Paper}>
        <Table sx={{ my: 2, minWidth: 650 }} aria-label="caption table">
          <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
            <TableCell sx={{ fontWeight: "bold" }} align="center">
              Sexo
            </TableCell>
            <TableCell sx={{ fontWeight: "bold" }}>Descrição</TableCell>
            <TableCell width="10%" sx={{ fontWeight: "bold" }}>
              Qtd Mín
            </TableCell>
            <TableCell sx={{ fontWeight: "bold" }} align="right">
              Idade Início
            </TableCell>
            <TableCell sx={{ fontWeight: "bold" }} align="center">
              Idade Final
            </TableCell>
            <TableCell sx={{ fontWeight: "bold" }} align="center">
              Tag
            </TableCell>
            <TableCell sx={{ fontWeight: "bold" }} align="center">
              Ações
            </TableCell>
          </TableRow>

          <TableBody>
            {listCategorias.map((row, idx) => (
              <TableRow
                key={idx}
                sx={{
                  "&:last-child td, &:last-child th": { border: 0 },
                  backgroundColor: getValoresTags(valoresTags, row.tagId)
                    ? ""
                    : colors.red[100],
                }}
              >
                <TableCell align="center">
                  {getSexo(listSexos, row.sexoId)}
                </TableCell>
                <TableCell>{row.descricao}</TableCell>
                <TableCell>{row.qtdMinima}</TableCell>
                <TableCell align="right">{row.idadeInicial}</TableCell>
                <TableCell align="center">{row.idadefinal}</TableCell>
                <TableCell align="center">
                  <MDBadge
                    variant="gradient"
                    color={getColorsTags(
                      getValoresTags(valoresTags, row.tagId).split(" - ")[0]
                    )}
                    size="xs"
                    badgeContent={getValoresTags(valoresTags, row.tagId)}
                    container
                  />
                </TableCell>

                <TableCell align="center" sx={{ display: "flex", gap: 1 }}>
                  <IconButton
                    color="default"
                    onClick={() => handleEditCategoria(idx)}
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton
                    color="error"
                    onClick={() => handleDeleteCategoria(idx)}
                  >
                    <DeleteOutlineIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </MDBox>
  );
}

//----------------------------------------------------------------------
// FUNÇÕES AUXILIARES
//----------------------------------------------------------------------

function getSexo(listSexos: any, sexoId: number) {
  if (!listSexos) return null;
  return listSexos.find((sexo: any) => sexo.id === Number(sexoId))?.tipo ?? "";
}

interface Tag {
  label: string;
  value: string;
}

/**
 * Função getTagsLotes:
 *
 * Esta função recebe um objeto contendo um array de lotes (do tipo ILotes[])
 * e retorna um array.
 *
 * - Se o array de lotes estiver vazio, a função retorna `null`.
 * - Caso contrário, a função itera sobre o array `valoresTags` do primeiro lote e monta as tags.
 *
 * @param {Object} lotes - Um objeto contendo o array de lotes (ILotes[]).
 * @returns {(Tag[] | null)} Retorna um array de `tags` ou `null` se não houver lotes.
 */
function getTagsLotes({ lotes }: { lotes: ILotes[] }): Tag[] | null {
  if (lotes.length === 0) return null;

  const firstLote = lotes[0];
  const tags: Tag[] = firstLote.valoresTags.map((tag) => ({
    label: `${tag.tag.tipo} - ${formatCurrency(tag.valor)}`,
    value: tag.tag.id.toString(),
  }));

  return tags;
}
